import { useCallback, useMemo, useState } from "react";
import { Modal, Select, Table, message } from "antd";
import type { TableColumnsType } from "antd";
import { BookOpen, ExternalLink, Plus, RefreshCw, Trash2 } from "lucide-react";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";

import { useGetCodeBooksQuery } from "graphql/queries/get-code-books.generated";
import { useGetJurisdictionCodeBooksQuery } from "graphql/queries/get-jurisdiction-code-books.generated";
import { useAdoptCodeBookMutation } from "graphql/mutations/adopt-code-book.generated";
import { useRemoveAdoptedCodeBookMutation } from "graphql/mutations/remove-adopted-code-book.generated";
import { useBackfillCodeCitationsMutation } from "graphql/mutations/backfill-code-citations.generated";

import { usePlanReviewJurisdiction } from "hooks/usePlanReviewJurisdiction";
import { usePlanReviewSettingsAccess } from "hooks/usePlanReviewSettingsAccess";
import { CodeBookType } from "constants/field-values";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildScreenDescription } from "utils/ollie-screen-description";

type Adoption = NonNullable<
	ReturnType<typeof useGetJurisdictionCodeBooksQuery>["data"]
>["jurisdiction_adopted_codes"][number];

// What the authority enforces each edition for. An AHJ commonly adopts IFC for fire and
// IBC for building at the same time, and this label is what tells them apart on screen.
const DISCIPLINE_OPTIONS = [
	{ value: "FIRE", label: "Fire" },
	{ value: "BUILDING", label: "Building" },
	{ value: "ELECTRICAL", label: "Electrical" },
	{ value: "MECHANICAL", label: "Mechanical" },
	{ value: "PLUMBING", label: "Plumbing" },
	{ value: "OTHER", label: "Other" }
];

const editionLabel = (book?: { abbreviation: string; edition_year?: number | null } | null): string =>
	book ? `${book.abbreviation}${book.edition_year ? ` ${book.edition_year}` : ""}` : "—";

/**
 * The code editions one authority enforces.
 *
 * This list is what gives a citation its meaning: "507.5.1" is resolved only against the
 * editions listed here, so an office that enforces the 2018 IFC never gets the 2021 text
 * quoted back to an applicant. Adopting an edition also adds its name to the rule
 * extraction prompt, so the AI cites the correct edition.
 */
const PlanReviewCodes = () => {
	const { jurisdictionId, picker, emptyReason } = usePlanReviewJurisdiction();
	// Tells Ollie what the reader is looking at, and for which authority.
	useDescribeScreenToOllie(
		buildScreenDescription({
			screen:
				"The adopted codes screen: which code books and editions this authority enforces, foundational model codes and its own local amendments. The reader maintains them here.",
			jurisdictionId
		}),
		{ jurisdictionId }
	);

	const { canManage, blockChange, notice } = usePlanReviewSettingsAccess();
	const [backfillCodeCitations, { loading: isResolvingCitations }] =
		useBackfillCodeCitationsMutation();

	/**
	 * Re-resolves this authority's rule citations against the editions it now enforces.
	 *
	 * There are two moments when a citation that resolved to nothing starts resolving to
	 * something: when a code book is published (handled automatically by the server), and
	 * when an authority adopts an edition that was already imported. This button covers
	 * the second case. It used to be a command run from a shell inside the task.
	 */
	const resolveCitations = useCallback(async () => {
		if (!canManage) return blockChange();
		if (!jurisdictionId) return;
		try {
			const { data: resolveData } = await backfillCodeCitations({
				variables: { jurisdictionId }
			});
			const summary = resolveData?.backfill_code_citations;
			if (!summary) return;
			message.success(
				`${summary.rules_linked} rules resolved, ${summary.before} citations before and ${summary.after} after.`
			);
		} catch (error: any) {
			// The server refuses a run that would leave the authority with far fewer links
			// than it had. That usually means the citation text was deleted from the rules,
			// or a code book was renamed. The message explains which, and nothing is
			// written.
			message.error(
				error?.graphQLErrors?.[0]?.message ??
					error?.message ??
					"The citations could not be resolved."
			);
		}
	}, [backfillCodeCitations, blockChange, canManage, jurisdictionId]);
	const lockedClass = canManage ? "" : " prcd-locked";

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [pickedBookId, setPickedBookId] = useState<string | undefined>();
	const [pickedDiscipline, setPickedDiscipline] = useState<string>(DISCIPLINE_OPTIONS[0].value);
	const [isSaving, setIsSaving] = useState(false);

	const {
		data: adoptedData,
		loading,
		error: loadError,
		refetch
	} = useGetJurisdictionCodeBooksQuery({
		skip: !jurisdictionId,
		variables: { jurisdictionId },
		fetchPolicy: "cache-and-network"
	});

	const { data: booksData } = useGetCodeBooksQuery({
		skip: !jurisdictionId,
		variables: { jurisdictionId },
		fetchPolicy: "cache-and-network"
	});

	const adopted = useMemo(() => adoptedData?.jurisdiction_adopted_codes ?? [], [adoptedData]);
	const adoptedIds = useMemo(() => new Set(adopted.map(row => row.code_book_id)), [adopted]);

	const available = useMemo(
		() => (booksData?.code_books ?? []).filter(book => !adoptedIds.has(book.id)),
		[booksData, adoptedIds]
	);

	const [adoptCodeBook] = useAdoptCodeBookMutation();
	const [removeAdoptedCodeBook] = useRemoveAdoptedCodeBookMutation();

	const openAdoptDialog = useCallback(() => {
		setPickedBookId(undefined);
		setPickedDiscipline(DISCIPLINE_OPTIONS[0].value);
		setIsModalOpen(true);
	}, []);

	const adoptSelectedCode = useCallback(async () => {
		if (!pickedBookId || !jurisdictionId) return;
		setIsSaving(true);
		try {
			await adoptCodeBook({
				variables: {
					object: {
						jurisdiction_id: jurisdictionId,
						code_book_id: pickedBookId,
						discipline: pickedDiscipline,
						sort_order: adopted.length
					}
				}
			});
			await refetch();
			setIsModalOpen(false);
			message.success("Code adopted. Rules can now cite its sections.");
		} catch (error) {
			console.error("Adopting the code book failed:", error);
			message.error("We couldn't adopt that code. Please try again.");
		} finally {
			setIsSaving(false);
		}
	}, [adoptCodeBook, refetch, jurisdictionId, pickedBookId, pickedDiscipline, adopted.length]);

	const removeAdoption = useCallback(
		async (adoption: Adoption) => {
			if (!canManage) return blockChange();
			try {
				await removeAdoptedCodeBook({
					variables: { jurisdictionId, codeBookId: adoption.code_book_id }
				});
				await refetch();
				message.success("Code removed from this jurisdiction.");
			} catch (error) {
				console.error("Removing the code book failed:", error);
				message.error("We couldn't remove that code. Please try again.");
			}
		},
		[removeAdoptedCodeBook, refetch, jurisdictionId, canManage, blockChange]
	);

	const columns: TableColumnsType<Adoption> = useMemo(
		() => [
			{
				title: "Code",
				key: "code",
				render: (_, row) => (
					<div>
						<p className="prcd-cell-title">
							<BookOpen size={14} />
							{editionLabel(row.code_book)}
							{row.code_book?.book_type === CodeBookType.AMENDMENT && (
								<span className="prcd-badge prcd-badge-amendment">Local amendment</span>
							)}
						</p>
						<p className="prcd-cell-sub">{row.code_book?.title}</p>
					</div>
				)
			},
			{
				title: "Enforced for",
				dataIndex: "discipline",
				key: "discipline",
				width: 150,
				render: (discipline: string | null) => (
					<span className="prcd-badge">
						{DISCIPLINE_OPTIONS.find(option => option.value === discipline)?.label ?? "—"}
					</span>
				)
			},
			{
				title: "Sections on file",
				key: "sections",
				width: 150,
				render: (_, row) => <span className="prcd-count">{row.code_book?.section_count ?? 0}</span>
			},
			{
				title: "Source",
				key: "source",
				width: 180,
				render: (_, row) =>
					row.code_book?.source_file_url ? (
						<a
							className="prcd-source"
							href={row.code_book.source_file_url}
							target="_blank"
							rel="noreferrer"
						>
							Open the PDF
							<ExternalLink size={13} />
						</a>
					) : (
						<span className="prcd-cell-sub">Not uploaded</span>
					)
			},
			{
				title: "",
				key: "actions",
				width: 60,
				render: (_, row) => (
					<button
						type="button"
						className={`prcd-icon-btn${lockedClass}`}
						aria-label={`Remove ${editionLabel(row.code_book)}`}
						onClick={() => removeAdoption(row)}
					>
						<Trash2 size={16} />
					</button>
				)
			}
		],
		[removeAdoption, lockedClass]
	);

	return (
		<>
			<OllieLayout>
				<div className="prcd-page">
					<div className="prcd-shell">
						<header className="prcd-header">
							<div>
								<h1 className="prcd-title">Adopted Codes</h1>
								<p className="prcd-subtitle">
									The code editions this authority enforces. A citation on one of your rules is resolved against
									these, and the section text is read alongside the rule when a submittal is screened.
								</p>
							</div>
							{canManage && (
								<div className="prcd-header-actions">
									{/* Disabled when the authority has adopted nothing: there would be
									    no edition to resolve the citations against. */}
									<button
										className="prcd-btn"
										onClick={resolveCitations}
										disabled={
											!jurisdictionId ||
											isResolvingCitations ||
											adopted.length === 0
										}
										title="Re-resolve this authority's rule citations against the editions it enforces"
									>
										<RefreshCw size={16} />
										{isResolvingCitations ? "Resolving…" : "Resolve citations"}
									</button>
									<button
										className="prcd-btn prcd-btn-primary"
										onClick={openAdoptDialog}
										disabled={!jurisdictionId}
									>
										<Plus size={16} />
										Adopt a code
									</button>
								</div>
							)}
						</header>

						<div className="prcd-header">{picker}</div>

						{notice}

						{loadError ? (
							<div className="prcd-empty">
								<p className="prcd-empty-title">We couldn&apos;t load the adopted codes</p>
								<p>{loadError.message}</p>
							</div>
						) : emptyReason ? (
							<div className="prcd-empty">
								<p className="prcd-empty-title">{emptyReason.title}</p>
								<p>{emptyReason.detail}</p>
							</div>
						) : !loading && adopted.length === 0 ? (
							<div className="prcd-empty">
								<p className="prcd-empty-title">No code adopted yet</p>
								<p>
									Until this jurisdiction adopts one, a rule citing &quot;IFC 507.5.1&quot; stays a line of text:
									nothing resolves it, and the analysis never reads the code behind the rule.
								</p>
							</div>
						) : (
							<Table
								className="prcd-table"
								rowKey="code_book_id"
								columns={columns}
								dataSource={adopted}
								loading={loading && adopted.length === 0}
								pagination={false}
							/>
						)}
					</div>
				</div>
			</OllieLayout>

			<Modal
				open={isModalOpen}
				onCancel={() => setIsModalOpen(false)}
				footer={null}
				closable={false}
				width={520}
				className="prcd-modal"
			>
				<h2 className="prcd-modal-title">Adopt a code</h2>
				<p className="prcd-modal-sub">
					Only editions already imported into the platform can be adopted. If the one this office enforces is
					missing, an administrator has to import it first.
				</p>

				<div className="prcd-field">
					<label className="prcd-label">Code edition</label>
					<Select
						className="prcd-control"
						value={pickedBookId}
						placeholder={available.length ? "Pick an edition" : "Every available edition is already adopted"}
						disabled={available.length === 0}
						options={available.map(book => ({
							value: book.id,
							label: `${editionLabel(book)} — ${book.title}`
						}))}
						onChange={setPickedBookId}
					/>
				</div>

				<div className="prcd-field">
					<label className="prcd-label">Enforced for</label>
					<Select
						className="prcd-control"
						value={pickedDiscipline}
						options={DISCIPLINE_OPTIONS}
						onChange={setPickedDiscipline}
					/>
				</div>

				<div className="prcd-modal-actions">
					<button type="button" className="prcd-btn" onClick={() => setIsModalOpen(false)} disabled={isSaving}>
						Cancel
					</button>
					<button
						type="button"
						className="prcd-btn prcd-btn-primary"
						onClick={adoptSelectedCode}
						disabled={isSaving || !pickedBookId}
					>
						{isSaving ? "Saving…" : "Adopt"}
					</button>
				</div>
			</Modal>
		</>
	);
};

export default PlanReviewCodes;
